import { CreateUserDto, CreateIdpProviderDto, UpdateIdpProviderDto, DataLakeStatusResponse, DatasetRowsResponse, DataLakeQueryResponse, AgentFeedback, ValidationResult, ToolConfigForAgent, TestResult, PromptTemplate, CreatePromptTemplateDto, StartAgentRunDto, StartAgentRunResponse, ConnectionTestBulkResult } from "../types/types";
import type { AiSkillHeader, AiSkill, CreateAiSkillDto, UpdateAiSkillDto } from '../types/aiSkills';
import type { DataAnalystExample } from '../types/dataAnalyst';
import type { Environment } from "../types/environment";
import { ImportSettingsPayload } from "../types/settingsImport";
import { SettingValueUpdateBody } from "../types/settings";
import { emitAuthExpired } from "./authEvents";
import { AiBuilderConversationFull, AiBuilderConversationSummary } from "../widget/types";
import type { UsageQueryParams } from "../pages/settingsTabs/aiUsageTab/types";
import { usageQuery } from "../pages/settingsTabs/aiUsageTab/usageQuery";

const configuredBase = import.meta.env.VITE_API_BASE_URL || '';


const BASE = (() => {
  if (!import.meta.env.DEV || !configuredBase) return configuredBase;
  try {
    const baseUrl = new URL(configuredBase, window.location.origin);
    return baseUrl.origin !== window.location.origin ? '/api' : configuredBase;
  } catch {
    return configuredBase;
  }
})();


async function api(path: string, init: RequestInit = {}, base?: string) {
  const headers = new Headers(init.headers || {});
  headers.set("Content-Type", "application/json");
  const res = await fetch(`${base || BASE}${path}`, { ...init, headers, credentials: "include" });

  if (!res.ok) {
    const contentType = res.headers.get("content-type") || "";
    let errorMessage = `${res.status} ${res.statusText}`;
    let userRole: number | undefined;

    try {
      if (contentType.includes("application/json")) {
        const errorData = await res.json();
        // NestJS error format: { message: string | string[] }
        if (errorData.message) {
          errorMessage = Array.isArray(errorData.message)
            ? errorData.message.join(", ")
            : errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string'
            ? errorData.error
            : errorData.error.message || JSON.stringify(errorData.error);
        }
        // Extract userRole if present in error response
        if (errorData.userRole !== undefined) {
          userRole = errorData.userRole;
        }
      } else {
        const text = await res.text();
        if (text) {
          errorMessage = text;
        }
      }
    } catch {
      // If parsing fails, use status text
    }
    if (res.status === 401) {
      emitAuthExpired();
    }

    if (res.status === 403) {
      errorMessage = "You don't have permission to perform this action";
    }
    const error = new Error(errorMessage);
    (error as any).status = res.status;
    if (userRole !== undefined) {
      (error as any).userRole = userRole;
    }
    throw error;
  }

  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

// --- AUTH ---
export const authApi = {
  login: (email: string, password: string) =>
    api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  clientLogin: (email: string, password: string, sessionId?: string) =>
    api("/oauth/login-oauth", { method: "POST", body: JSON.stringify({ email, password, sessionId }) }),
  continueSession: (sessionId: string) =>
    api("/oauth/continue-session", { method: "POST", body: JSON.stringify({ sessionId }) }),
  logout: () => api("/auth/logout", { method: "POST" }),
  getCurrentUser: () => api("/auth/me"),
  register: (userData: any) =>
    api("/auth/register", { method: "POST", body: JSON.stringify(userData) }),
  getApprovalStatus: () => api("/auth/approval-status"),
};

// --- USERS ---
export const usersApi = {
  list: () => api("/users"),
  create: (userData: CreateUserDto) => api("/users/create", { method: "POST", body: JSON.stringify(userData) }),
  update: (id: string, userData: any) => api(`/users/${id}`, { method: "POST", body: JSON.stringify(userData) }),
  delete: (id: string) => api(`/users/${id}`, { method: "DELETE" }),
  activate: (id: string, roleId?: number) => api(`/users/${id}/activate`, { method: "POST", body: JSON.stringify({ roleId }) }),
};

// --- API KEYS ---
export const apiKeysApi = {
  list: () => api("/api-keys"),
  create: (keyData: any) => api("/api-keys", { method: "POST", body: JSON.stringify(keyData) }),
  revoke: (id: number) => api(`/api-keys/${id}`, { method: "DELETE" }),
};

// --- CONFIG ---
export const configApi = {
  list: () => api("/connectors?decrypt=true"),
  get: (userId: string, configId: string) => api(`/connectors/userId=${userId}&configId=${configId}&decrypt=true`),
  getBasic: () => api(`/connectors/basic`),
  put: (id: string | number, body: any) => api(`/connectors/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  post: (body: any) => api("/connectors", { method: "POST", body: JSON.stringify(body) }),
  testConnection: (id: string | number, connectionData?: any) =>
    api(`/connectors/test-connection/${id}`, {
      method: "POST",
      body: connectionData ? JSON.stringify(connectionData) : undefined
    }),
  testBasicConnection: (id: string | number) => api(`/connectors/test-connection/check/${id}`, { method: "POST", }),
  testConnectionsBulk: (ids: (string | number)[]): Promise<ConnectionTestBulkResult[]> =>
    api(`/connectors/test-connection/check-bulk`, { method: "POST", body: JSON.stringify({ ids }) }),
  getDbSchema: (id: string) => api(`/connectors/${id}/db-schema`),
  duplicate: (id: string, connectionName: string, environment?: Environment) =>
    api(`/connectors/duplicate/${id}?decrypt=true`, {
      method: "POST",
      body: JSON.stringify({ connectionName, environment })
    }),
  delete: (id: string) => api(`/connectors/${id}`, { method: "DELETE" }),
  setActive: (id: string, active: boolean) =>
    api(`/connectors/${id}/active`, { method: 'PUT', body: JSON.stringify({ active }) }),
};

// --- TOOLS ---
export const toolsApi = {
  list: (page: number = 1, take: number = 20) => api(`/tools?page=${page}&take=${take}`),
  get: (id: string | number) => api(`/tools/${id}`),
  create: (tool: any) => api("/tools/add", { method: "POST", body: JSON.stringify(tool) }),
  update: (id: string | number, tool: any) => api(`/tools/edit/${id}`, { method: "PUT", body: JSON.stringify(tool) }),
  delete: (id: string | number) => api(`/tools/delete/${id}`, { method: "DELETE" }),
  bulkDelete: (ids: number[]) => api("/tools/bulk-delete", { method: "DELETE", body: JSON.stringify({ ids }) }),
  duplicate: (id: string | number, displayName: string, technicalName?: string) =>
    api(`/tools/duplicate/${id}`, { method: "POST", body: JSON.stringify({ name: technicalName, title: displayName }) }),
  bulkDuplicate: (tools: Array<{ id: number; name: string; title: string }>) =>
    api('/tools/bulk-duplicate', { method: 'POST', body: JSON.stringify({ tools }) }),
  dryRun: (id: string | number, payload: { inputs: any, execute?: boolean }) =>
    api(`tools/${id}/dry-run`, { method: "POST", body: JSON.stringify(payload) }),
  importPackage: (payload: { package: any; tools: any[]; connectionId: string; mode?: string }) =>
    api("/tools/import-package", { method: "POST", body: JSON.stringify(payload) }),
  exportPackage: (toolIds: (string | number)[]) =>
    api("/tools/export-package", { method: "POST", body: JSON.stringify({ toolIds }) }),
};

// --- TOOL PACKAGES ---
export const toolPackagesApi = {
  list: (enabledOnly?: boolean) => api(`/tool-packages${enabledOnly ? '?enabled=true' : ''}`),
  get: (id: string) => api(`/tool-packages/${id}`),
  create: (data: { name: string; description?: string; version?: string; connectionTypeId: number; tools: any[] }) =>
    api("/tool-packages", { method: "POST", body: JSON.stringify(data) }),
  updateName: (id: string, name: string) =>
    api(`/tool-packages/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  toggleEnabled: (id: string, enabled: boolean) =>
    api(`/tool-packages/${id}/enabled`, { method: "PUT", body: JSON.stringify({ enabled }) }),
  delete: (id: string) => api(`/tool-packages/${id}`, { method: "DELETE" }),
};

// --- LOGS ---
export const logsApi = {
  get: (page: number = 1, take: number = 20, level?: string, search?: string, date?: string, source?: string, fromTime?: string, toTime?: string, user?: string) => {
    const params = new URLSearchParams({ page: String(page), take: String(take) });
    if (level) params.append('level', level);
    if (search) params.append('search', search);
    if (date || fromTime || toTime) {
      const [year, month, day] = (date || new Date().toLocaleDateString('en-CA')).split('-').map(Number);
      const [startHour, startMinute] = fromTime ? fromTime.split(':').map(Number) : [0, 0];
      const [endHour, endMinute] = toTime ? toTime.split(':').map(Number) : [23, 59];
      const localStart = new Date(year, month - 1, day, startHour, startMinute, 0, 0);
      const localEnd = new Date(year, month - 1, day, endHour, endMinute, 59, 999);
      params.append('from', localStart.toISOString());
      params.append('to', localEnd.toISOString());
    }
    if (source) params.append('source', source);
    if (user) params.append('user', user);
    return api(`/logs?${params.toString()}`);
  },
  bulkDelete: (ids: number[]) => api("/logs/bulk-delete", { method: "DELETE", body: JSON.stringify({ ids }) }),
  clearAll: () => api("/logs/clear", { method: "DELETE" }),
  getDailyToolUsage: (limit: number = 200) => {
    const params = new URLSearchParams({ limit: String(limit) });
    return api(`/logs/chart/daily-tool-usage?${params.toString()}`);
  },
};

// --- AI ---

export const aiApi = {
  generateContent: (prompt: { type: string; description: string; context?: any }) =>
    api("/llm/messages", { method: "POST", body: JSON.stringify(prompt) }),
  callClaude: (prompt: string) =>
    api("/messages", { method: "POST", body: JSON.stringify({ prompt }) }),
  toolBuilderChat: (
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    signal?: AbortSignal,
    agentFeedback?: AgentFeedback,
    connectorId?: string | null,
  ) =>
    api("/llm/tool-builder", {
      method: "POST",
      body: JSON.stringify({ messages, agentFeedback, connectorId: connectorId ?? undefined }),
      signal,
    }),
  toolBuilderChatStream: async (
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    onChunk: (accumulated: string) => void,
    signal?: AbortSignal,
    agentFeedback?: AgentFeedback,
    connectorId?: string | null,
    mode?: 'tool-builder' | 'skill-meta',
  ): Promise<string> => {
    const res = await fetch(`${BASE}/llm/tool-builder-stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ messages, agentFeedback, connectorId: connectorId ?? undefined, mode }),
      signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      const err = new Error(text || `${res.status} ${res.statusText}`);
      (err as any).status = res.status;
      throw err;
    }
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulated = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') continue;
        try {
          const event = JSON.parse(data) as { t: string };
          accumulated += event.t;
          onChunk(accumulated);
        } catch { /* ignore malformed lines */ }
      }
    }
    return accumulated;
  },
};

// --- HEALTH ---
export const healthApi = {
  health: () => api("/health"),
};

// --- AI SKILLS ---
export const aiSkillsApi = {
  getHeaders: (connectorTypeId?: number): Promise<AiSkillHeader[]> => {
    const qs = connectorTypeId !== undefined ? `?connectorTypeId=${connectorTypeId}` : '';
    return api(`/ai-skills/headers${qs}`);
  },
  getContent: (keys: string[]): Promise<AiSkill[]> => {
    if (!keys.length) return Promise.resolve([]);
    return api(`/ai-skills/content?keys=${keys.join(',')}`);
  },
  list: (): Promise<AiSkill[]> => api('/ai-skills'),
  create: (dto: CreateAiSkillDto): Promise<AiSkill> =>
    api('/ai-skills', { method: 'POST', body: JSON.stringify(dto) }),
  update: (key: string, dto: UpdateAiSkillDto): Promise<AiSkill> =>
    api(`/ai-skills/${key}`, { method: 'PATCH', body: JSON.stringify(dto) }),
  delete: (key: string): Promise<{ deleted: boolean }> =>
    api(`/ai-skills/${key}`, { method: 'DELETE' }),
};

// --- TAGS ---
export const tagsApi = {
  list: () => api("/tags"),
  listWithCount: () => api("/tags/with-counts"),
  create: (name: string) => api("/tags", { method: "POST", body: JSON.stringify({ name }) }),
  delete: (id: number) => api(`/tags/${id}`, { method: "DELETE" }),
  getByName: (name: string) => api(`/tags/name/${encodeURIComponent(name)}`),
  getById: (id: number) => api(`/tags/${id}`),
  rename: (id: number, name: string) => api(`/tags/${id}`, { method: "PUT", body: JSON.stringify({ name }) }),
};

// --- IDP PROVIDERS ---
export const idpProvidersApi = {
  list: () => api("/idp/providers"),
  listAdmin: () => api("/idp/providers/admin"),
  create: (data: CreateIdpProviderDto) =>
    api("/idp/provider", { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateIdpProviderDto) =>
    api(`/idp/provider/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    api(`/idp/provider/${id}`, { method: 'DELETE' }),
  getUrl: (providerId: string, redirect_uri: string, sessionId: string) =>
    api(`/idp/login-url`, { method: 'POST', body: JSON.stringify({ providerId, redirect_uri, sessionId }) }),
  toggleActive: (id: string, active: boolean) =>
    api(`/idp/provider/${id}/active`, { method: 'PUT', body: JSON.stringify({ active }) }),
};

// --- OTP CONFIG ---
export const otpConfigApi = {
  get: () => api('/webchat/otp/config'),
  update: (config: {
    otpEnabled?: boolean;
    otpProvider?: string;
    smtpHost?: string;
    smtpPort?: number;
    smtpUser?: string;
    smtpPass?: string;
    smtpFrom?: string;
  }) => api('/webchat/otp/config', { method: 'PUT', body: JSON.stringify(config) }),
  test: (data: {
    recipientEmail: string;
    smtpHost?: string;
    smtpPort?: number;
    smtpUser?: string;
    smtpPass?: string;
    smtpFrom?: string;
  }) => api('/webchat/otp/test', { method: 'POST', body: JSON.stringify(data) }),
};

// --- CHAT HISTORY ---
export const chatHistoryApi = {
  list: (page: number = 1, take: number = 20, filters?: { status?: string; search?: string; userId?: string; dateFrom?: string; dateTo?: string; widgetId?: string }) => {
    const params = new URLSearchParams({ page: String(page), take: String(take) });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.widgetId) params.append('widgetId', filters.widgetId);
    return api(`/webchat/conversations?${params.toString()}`);
  },
  get: (id: string) => api(`/webchat/conversations/${id}`),
  delete: (id: string) => api(`/webchat/conversations/${id}`, { method: 'DELETE' }),
  bulkDelete: (ids: string[]) => api('/webchat/conversations/bulk-delete', { method: 'DELETE', body: JSON.stringify({ ids }) }),
  clearAll: (widgetId?: string) => {
    const qs = widgetId ? `?widgetId=${encodeURIComponent(widgetId)}` : '';
    return api(`/webchat/conversations/clear${qs}`, { method: 'DELETE' });
  },
  disableHistory: () => api('/webchat/conversations/disable-history', { method: 'POST' }),
};

// --- WEBCHAT ANALYTICS ---
export const webchatAnalyticsApi = {
  get: (dateFrom?: string, dateTo?: string, widgetId?: string) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (widgetId) params.append('widgetId', widgetId);
    const qs = params.toString();
    return api(`/webchat/analytics${qs ? `?${qs}` : ''}`);
  },
};

// --- WEBCHAT WIDGETS ---
export const widgetsApi = {
  list: () => api('/webchat/widgets').then((res: any) => res.widgets ?? res),
  get: (id: string) => api(`/webchat/widgets/${id}`),
  create: (data: { name: string; config: Record<string, any> }) =>
    api('/webchat/widgets', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: { name?: string; isActive?: boolean; config?: Record<string, any> }) =>
    api(`/webchat/widgets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => api(`/webchat/widgets/${id}`, { method: 'DELETE' }),
  toggle: (id: string) => api(`/webchat/widgets/${id}/toggle`, { method: 'PATCH' }),
  duplicate: (id: string, name: string) =>
    api(`/webchat/widgets/${id}/duplicate`, { method: 'POST', body: JSON.stringify({ name }) }),
  getTokenUsage: (id: string) => api(`/webchat/widgets/${id}/token-usage`),
};

// --- SETTINGS ---
export const settingsValuesApi = {
  setValue: (definitionId: number, body: SettingValueUpdateBody) =>
    api(`/settings-values/${definitionId}`, { method: 'PATCH', body: JSON.stringify(body) }),
};

export const settingsDefinitionsApi = {
  getDefinitions: () => api('/settings/definitions'),
  createDefinition: (def: any) =>
    api('/settings/definitions', { method: 'POST', body: JSON.stringify(def) }),
  updateDefinition: (id: number, def: any) =>
    api(`/settings/definitions/${id}`, { method: 'PATCH', body: JSON.stringify(def) }),
  // Soft delete is modeled as deactivation.
  softDeleteDefinition: (id: number) =>
    api(`/settings/definitions/${id}`, { method: 'PATCH', body: JSON.stringify({ isActive: false }) }),
  hardDeleteDefinition: (id: number) =>
    api(`/settings/definitions/${id}`, { method: 'DELETE' }),
  importDefinitions: (definitions: any[]) =>
    api('/settings/definitions/import', { method: 'POST', body: JSON.stringify({ definitions }) }),
  getDefinitionValues: (id: number) =>
    api(`/settings/definitions/${id}/values`),
};

export const settingsApi = {
  // Resolved values
  getResolved: (category?: string) => category ? api(`/settings/resolved?category=${encodeURIComponent(category)}`) : api('/settings/resolved'),
  setValue: (key: string, body: SettingValueUpdateBody) =>
    api(`/settings/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // // Definitions (Backwards-compatible facade)
  // getDefinitions: settingsDefinitionsApi.getDefinitions,
  // createDefinition: settingsDefinitionsApi.createDefinition,
  // updateDefinition: settingsDefinitionsApi.updateDefinition,
  // deleteDefinition: settingsDefinitionsApi.softDeleteDefinition,
  // hardDeleteDefinition: settingsDefinitionsApi.hardDeleteDefinition,
  // importDefinitions: settingsDefinitionsApi.importDefinitions,
  // getDefinitionValues: settingsDefinitionsApi.getDefinitionValues,

  // LLM / MCP result truncation settings
  getLlmSettings: () => api('/settings/category/llm'),
  // Webchat settings (Chat LLM + Chat Voice)
  getWebchatSettings: () => api('/settings/category/webchat'),
  getAiBuilderSettings: () => api('/settings/category/ai_builder'),
  saveLlmSettings: (values: Record<string, string | number>) =>
    api('/settings/llm', { method: 'PATCH', body: JSON.stringify(values) }),

  // DataLake settings
  getDataLakeSettings: () => api('/settings/data-lake'),
  saveDataLakeSettings: (values: Record<string, string | number | boolean>) =>
    api('/settings/data-lake', { method: 'PATCH', body: JSON.stringify(values) }),

  // Connectors management
  getConnectors: () => api('/settings/connectors'),

  // Bulk import (SuperAdmin) - mode: 'replace' drops all existing, 'merge' adds only missing
  importSettings: (payload: ImportSettingsPayload) =>
    api('/settings/import', { method: 'POST', body: JSON.stringify(payload) }),
};

export const dataLakeMonitorApi = {
  getStatus: (): Promise<DataLakeStatusResponse> =>
    api('/data-lake/status'),
  dropSession: (sessionId: string): Promise<{ success: boolean }> =>
    api(`/data-lake/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' }),
  getDatasetRows: (
    sessionId: string,
    datasetId: string,
    page: number,
    pageSize: number,
  ): Promise<DatasetRowsResponse> =>
    api(`/data-lake/sessions/${encodeURIComponent(sessionId)}/datasets/${encodeURIComponent(datasetId)}/rows?page=${page}&pageSize=${pageSize}`),
  runQuery: (sessionId: string, datasetId: string, sql: string): Promise<DataLakeQueryResponse> =>
    api(`/data-lake/sessions/${encodeURIComponent(sessionId)}/datasets/${encodeURIComponent(datasetId)}/query`, {
      method: 'POST',
      body: JSON.stringify({ sql }),
    }),
};

export const dataAnalystExamplesApi = {
  list: (): Promise<DataAnalystExample[]> => api('/webchat/data-analyst/examples'),
  remove: (id: number): Promise<{ success: boolean }> =>
    api(`/webchat/data-analyst/examples/${id}`, { method: 'DELETE' }),
};

export const promptTemplatesApi = {
  list: (): Promise<PromptTemplate[]> => api('/prompt-templates'),
  create: (dto: CreatePromptTemplateDto): Promise<PromptTemplate> =>
    api('/prompt-templates', { method: 'POST', body: JSON.stringify(dto) }),
  update: (key: string, content: string): Promise<PromptTemplate> =>
    api(`/prompt-templates/${key}`, { method: 'PUT', body: JSON.stringify({ content }) }),
  reset: (key: string): Promise<PromptTemplate> =>
    api(`/prompt-templates/${key}/reset`, { method: 'POST' }),
  delete: (key: string): Promise<void> =>
    api(`/prompt-templates/${key}`, { method: 'DELETE' }),
};

// --- AI BUILDER CONVERSATIONS ---

export const aiBuilderApi = {
  listConversations: (toolId: number | null): Promise<AiBuilderConversationSummary[]> =>
    api(`/ai-builder/conversations?toolId=${toolId ?? 'null'}`),
  getConversation: (id: string): Promise<AiBuilderConversationFull> =>
    api(`/ai-builder/conversations/${id}`),
  createConversation: (data: { messages: any[]; toolId: number | null }): Promise<AiBuilderConversationFull> =>
    api('/ai-builder/conversations', { method: 'POST', body: JSON.stringify(data) }),
  updateConversation: (id: string, data: { messages: any[]; toolId: number | null }): Promise<AiBuilderConversationFull> =>
    api(`/ai-builder/conversations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteConversation: (id: string): Promise<void> =>
    api(`/ai-builder/conversations/${id}`, { method: 'DELETE' }),
  validateConfig: (toolConfig: ToolConfigForAgent): Promise<ValidationResult> =>
    api('/ai-builder/validate', { method: 'POST', body: JSON.stringify({ toolConfig }) }),
  testTool: (toolConfig: ToolConfigForAgent, testArgs?: Record<string, unknown>): Promise<TestResult> =>
    api('/ai-builder/test-tool', { method: 'POST', body: JSON.stringify({ toolConfig, testArgs }) }),
};

// --- AGENT RUN (SSE-based agent loop) ---

export const agentRunApi = {
  startRun: (dto: StartAgentRunDto): Promise<StartAgentRunResponse> =>
    api('/ai-builder/agent-run', { method: 'POST', body: JSON.stringify(dto) }),
  approve: (runId: string, approved: boolean): Promise<{ ok: boolean }> =>
    api(`/ai-builder/agent-run/${encodeURIComponent(runId)}/approve`, { method: 'POST', body: JSON.stringify({ approved }) }),
  abort: (runId: string): Promise<{ ok: boolean }> =>
    api(`/ai-builder/agent-run/${encodeURIComponent(runId)}`, { method: 'DELETE' }),
  streamEvents: (runId: string, ticket: string): EventSource =>
    new EventSource(`${BASE}/ai-builder/agent-run/${encodeURIComponent(runId)}/events?ticket=${encodeURIComponent(ticket)}`),
};

// --- AI BUILDER USAGE ---
export const aiUsageApi = {
  getSummary: (p?: UsageQueryParams) => api(`/ai-builder/usage/summary${usageQuery(p)}`),
  getDaily: (p?: UsageQueryParams) => api(`/ai-builder/usage/daily${usageQuery(p)}`),
  getRecords: (p?: UsageQueryParams) => api(`/ai-builder/usage/records${usageQuery(p)}`),
};

// --- TOOL FILTER PRESETS ---
export const toolFilterPresetsApi = {
  list: (): Promise<any[]> => api('/tool-filter-presets'),
  create: (name: string, filters: object): Promise<any> =>
    api('/tool-filter-presets', { method: 'POST', body: JSON.stringify({ name, filters }) }),
  update: (id: string, data: { name?: string; filters?: object }): Promise<any> =>
    api(`/tool-filter-presets/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string): Promise<void> =>
    api(`/tool-filter-presets/${id}`, { method: 'DELETE' }),
};
